/* eslint-disable */
import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import OwnerTour from "../components/OwnerTour";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

const ALL_AMENITIES = ["wifi", "standingDesk", "phoneBooth", "parking"];

function fmtMoney(n) {
  const v = Number(n) || 0;
  return `$${v.toFixed(v >= 100 ? 0 : 2)}`;
}

function PhotoUploader({ wsId, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image too large (max 6 MB)");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const r = await api.post(`/owner/workspaces/${wsId}/photo`, {
        filename: file.name, data_url: dataUrl,
      });
      onUploaded(r.data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        type="file" accept="image/png,image/jpeg,image/webp"
        ref={inputRef} onChange={onPick}
        style={{ display: "none" }}
        data-testid={`owner-upload-input-${wsId}`}
      />
      <button
        type="button" className="m-btn-ghost" disabled={busy}
        onClick={() => inputRef.current?.click()}
        data-testid={`owner-upload-btn-${wsId}`}
      >
        {busy ? "Uploading…" : "Upload photo"}
      </button>
    </>
  );
}

function WorkspaceEditor({ ws, onSaved }) {
  const [form, setForm] = useState({
    name: ws.name || "",
    neighborhood: ws.neighborhood || "",
    address: ws.address || "",
    type: ws.type || "Coworking Space",
    description: ws.description || "",
    hourly: ws.hourly || 0,
    daily: ws.daily || 0,
    weekly: ws.weekly || 0,
    monthly: ws.monthly || 0,
    today: ws.today !== false,
    amenities: ws.amenities || [],
    work_intent_tags: ws.work_intent_tags || [],
    gallery: (ws.gallery && ws.gallery.length ? ws.gallery : [ws.img || ""]),
  });
  const [busy, setBusy] = useState(false);
  const [workIntents, setWorkIntents] = useState([]);
  useEffect(() => {
    api.get("/options/work-intents").then(r => setWorkIntents(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleAm = (a) => set("amenities", (form.amenities || []).includes(a) ? (form.amenities || []).filter(x => x !== a) : [...(form.amenities || []), a]);
  const toggleIntent = (k) => set("work_intent_tags", (form.work_intent_tags || []).includes(k) ? (form.work_intent_tags || []).filter(x => x !== k) : [...(form.work_intent_tags || []), k]);
  const setPhoto = (i, v) => {
    const g = [...form.gallery]; g[i] = v;
    set("gallery", g);
  };
  const addSlot = () => set("gallery", [...form.gallery, ""]);
  const removePhoto = (i) => {
    const g = form.gallery.filter((_, idx) => idx !== i);
    set("gallery", g.length ? g : [""]);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cleanedGallery = form.gallery.map(s => (s || "").trim()).filter(Boolean);
      const payload = {
        name: form.name,
        neighborhood: form.neighborhood,
        address: form.address,
        type: form.type,
        description: form.description,
        hourly: Number(form.hourly) || 0,
        daily: Number(form.daily) || 0,
        weekly: Number(form.weekly) || 0,
        monthly: Number(form.monthly) || 0,
        today: !!form.today,
        amenities: form.amenities,
        work_intent_tags: form.work_intent_tags,
        gallery: cleanedGallery,
      };
      const r = await api.put(`/owner/workspaces/${ws.workspace_id}`, payload);
      onSaved(r.data);
      toast.success("Workspace updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSave} className="m-form" data-testid={`owner-editor-${ws.workspace_id}`}>
      <div className="m-form-grid-2">
        <label className="m-field"><span>Workspace name</span>
          <input value={form.name} onChange={e => set("name", e.target.value)} required data-testid={`owner-name-${ws.workspace_id}`} />
        </label>
        <label className="m-field"><span>Type</span>
          <select value={form.type} onChange={e => set("type", e.target.value)} data-testid={`owner-type-${ws.workspace_id}`}>
            {["Coworking Space", "Private Office", "Meeting Room", "Event Venue"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>
      <div className="m-form-grid-2">
        <label className="m-field"><span>Neighborhood</span>
          <input value={form.neighborhood} onChange={e => set("neighborhood", e.target.value)} required data-testid={`owner-neighborhood-${ws.workspace_id}`} />
        </label>
        <label className="m-field"><span>Available today?</span>
          <select value={form.today ? "yes" : "no"} onChange={e => set("today", e.target.value === "yes")} data-testid={`owner-today-${ws.workspace_id}`}>
            <option value="yes">Yes, accepting bookings</option>
            <option value="no">No, hide availability badge</option>
          </select>
        </label>
      </div>
      <label className="m-field"><span>Street address</span>
        <input value={form.address} onChange={e => set("address", e.target.value)} placeholder="e.g. 60 Commercial St, Concord, NH 03301" data-testid={`owner-address-${ws.workspace_id}`} />
      </label>
      <label className="m-field"><span>Description</span>
        <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={4}
          style={{ padding: "12px 14px", border: "1px solid var(--m-border)", borderRadius: 9, background: "var(--cream)", fontSize: 14, resize: "vertical", color: "var(--m-text)" }}
          data-testid={`owner-description-${ws.workspace_id}`} />
      </label>

      <h4 className="rec-h3" style={{ marginTop: 10, marginBottom: 4 }}>Rates</h4>
      <div className="m-form-grid-2">
        <label className="m-field"><span>Hourly $</span><input type="number" step="0.01" value={form.hourly} onChange={e => set("hourly", e.target.value)} data-testid={`owner-hourly-${ws.workspace_id}`} /></label>
        <label className="m-field"><span>Daily $</span><input type="number" step="0.01" value={form.daily} onChange={e => set("daily", e.target.value)} data-testid={`owner-daily-${ws.workspace_id}`} /></label>
      </div>
      <div className="m-form-grid-2">
        <label className="m-field"><span>Weekly $</span><input type="number" step="0.01" value={form.weekly} onChange={e => set("weekly", e.target.value)} data-testid={`owner-weekly-${ws.workspace_id}`} /></label>
        <label className="m-field"><span>Monthly $</span><input type="number" step="0.01" value={form.monthly} onChange={e => set("monthly", e.target.value)} data-testid={`owner-monthly-${ws.workspace_id}`} /></label>
      </div>
      <p className="m-muted small">Hourly applies under 8h · Daily ≥ 8h · Weekly ≥ 7 days · Monthly ≥ 30 days.</p>

      <h4 className="rec-h3" style={{ marginTop: 10, marginBottom: 4 }}>Amenities</h4>
      <div className="m-amenities">
        {ALL_AMENITIES.map(a => (
          <button key={a} type="button"
            className={"m-pill light" + ((form.amenities || []).includes(a) ? " active" : "")}
            onClick={() => toggleAm(a)} data-testid={`owner-am-${ws.workspace_id}-${a}`}>{a}</button>
        ))}
      </div>

      <h4 className="rec-h3" style={{ marginTop: 14, marginBottom: 4 }}>Best For</h4>
      <p className="m-muted small" style={{ marginTop: -2 }}>Tell members what kinds of work this room is at its best for. Members can filter the directory by these tags.</p>
      <div className="m-amenities">
        {workIntents.map(w => (
          <button key={w.key} type="button"
            className={"m-pill light" + ((form.work_intent_tags || []).includes(w.key) ? " active" : "")}
            onClick={() => toggleIntent(w.key)} data-testid={`owner-intent-${ws.workspace_id}-${w.key}`}>{w.label}</button>
        ))}
      </div>

      <h4 className="rec-h3" style={{ marginTop: 14, marginBottom: 4 }}>Photos</h4>
      <p className="m-muted small" style={{ marginTop: -2 }}>Paste URLs (Pexels / Unsplash / your own host) or upload directly. The first photo is the cover image.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {form.gallery.map((p, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 6, alignItems: "center" }}>
            <input value={p} onChange={e => setPhoto(i, e.target.value)} placeholder="https://… or /api/uploads/…" data-testid={`owner-photo-${ws.workspace_id}-${i}`}
              style={{ padding: "10px 12px", border: "1px solid var(--m-border)", borderRadius: 9, background: "var(--cream)", fontSize: 13 }} />
            <PhotoUploader wsId={ws.workspace_id} onUploaded={(url) => setPhoto(i, url)} />
            <button type="button" className="m-btn-ghost" onClick={() => removePhoto(i)} aria-label="remove" data-testid={`owner-photo-rm-${ws.workspace_id}-${i}`}>×</button>
          </div>
        ))}
        <button type="button" className="m-btn-ghost" onClick={addSlot} data-testid={`owner-photo-add-${ws.workspace_id}`} style={{ alignSelf: "flex-start" }}>+ Add photo</button>
      </div>

      <div className="m-form-actions" style={{ marginTop: 18 }}>
        <Link to={`/spaces/${ws.slug}`} className="m-btn-ghost" target="_blank" rel="noreferrer" data-testid={`owner-view-${ws.workspace_id}`} style={{ textDecoration: "none" }}>View live page →</Link>
        <button type="submit" className="m-btn-gold" disabled={busy} data-testid={`owner-save-${ws.workspace_id}`}>{busy ? "Saving…" : "Save changes"}</button>
      </div>
    </form>
  );
}

function BookingControlsEditor({ wsId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/owner/workspaces/${wsId}/booking-settings`);
      setData(r.data);
      const s = r.data.settings;
      setForm({
        booking_mode: s.booking_mode || "instant",
        buffer_before_override: !!s.buffer_before_override,
        buffer_after_override: !!s.buffer_after_override,
        buffer_before_minutes: s.buffer_before_minutes,
        buffer_after_minutes: s.buffer_after_minutes,
        min_notice_hours: s.min_notice_hours,
        max_advance_days: s.max_advance_days,
        cancellation_policy: s.cancellation_policy || "moderate",
        cancellation_policy_text: s.cancellation_policy_text || "",
        request_expiry_hours: s.request_expiry_hours || 24,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load booking settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  if (loading || !form) {
    return <div className="m-loading-inline" data-testid={`owner-controls-loading-${wsId}`}>Loading booking controls…</div>;
  }

  const nd = data.network_defaults;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        booking_mode: form.booking_mode,
        buffer_before_minutes: form.buffer_before_override ? Number(form.buffer_before_minutes) : null,
        buffer_after_minutes: form.buffer_after_override ? Number(form.buffer_after_minutes) : null,
        min_notice_hours: Number(form.min_notice_hours),
        max_advance_days: Number(form.max_advance_days),
        cancellation_policy: form.cancellation_policy,
        cancellation_policy_text: form.cancellation_policy === "custom" ? form.cancellation_policy_text : undefined,
        request_expiry_hours: Number(form.request_expiry_hours),
      };
      await api.put(`/owner/workspaces/${wsId}/booking-settings`, payload);
      toast.success("Booking controls saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const wasConfigured = data?.settings?.configured;

  return (
    <form onSubmit={onSave} className="m-form" data-testid={`owner-controls-${wsId}`}>
      {!wasConfigured && (
        <div className="m-callout" style={{ padding: "12px 16px", background: "rgba(184,145,58,0.08)", border: "1px solid rgba(184,145,58,0.3)", borderRadius: 9, marginBottom: 14, fontSize: 13.5, color: "var(--m-text)" }} data-testid={`owner-controls-unconfigured-${wsId}`}>
          <b>Set your booking rules to start accepting reservations.</b><br/>
          Until you save this form once, this workspace is blocked from new bookings.
        </div>
      )}

      <h4 className="rec-h3" style={{ marginTop: 4, marginBottom: 4 }}>Booking mode</h4>
      <p className="m-muted small" style={{ marginTop: -2 }}>Decide whether members can book instantly or need your approval first.</p>
      <div className="m-amenities" data-testid={`owner-controls-mode-${wsId}`}>
        {[{k:"instant", label:"Instant book"}, {k:"request", label:"Request to book"}].map(o => (
          <button key={o.k} type="button"
            className={"m-pill light" + (form.booking_mode === o.k ? " active" : "")}
            onClick={() => set("booking_mode", o.k)}
            data-testid={`owner-controls-mode-${o.k}-${wsId}`}>{o.label}</button>
        ))}
      </div>

      {form.booking_mode === "request" && (
        <label className="m-field" style={{ marginTop: 12 }}><span>Auto-decline requests after (hours)</span>
          <input type="number" min="1" max="168" value={form.request_expiry_hours}
            onChange={e => set("request_expiry_hours", e.target.value)}
            data-testid={`owner-controls-expiry-${wsId}`} />
          <span className="m-muted small">Members are automatically refunded when a request expires. Network default: 24 hours.</span>
        </label>
      )}

      <h4 className="rec-h3" style={{ marginTop: 18, marginBottom: 4 }}>Availability buffers</h4>
      <p className="m-muted small" style={{ marginTop: -2 }}>
        Network default is {nd.buffer_before_minutes} min before and {nd.buffer_after_minutes} min after every booking.
        Override per workspace below, or set either to 0 to disable.
      </p>
      <div className="m-form-grid-2">
        <label className="m-field">
          <span>Buffer <b>before</b> booking</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={form.buffer_before_override}
              onChange={e => set("buffer_before_override", e.target.checked)}
              data-testid={`owner-controls-buffer-before-override-${wsId}`}
              style={{ width: 16, height: 16 }} />
            <span className="m-muted small">Override network default</span>
          </div>
          <input type="number" min="0" max="240"
            value={form.buffer_before_override ? form.buffer_before_minutes : nd.buffer_before_minutes}
            disabled={!form.buffer_before_override}
            onChange={e => set("buffer_before_minutes", e.target.value)}
            data-testid={`owner-controls-buffer-before-${wsId}`} />
        </label>
        <label className="m-field">
          <span>Buffer <b>after</b> booking</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={form.buffer_after_override}
              onChange={e => set("buffer_after_override", e.target.checked)}
              data-testid={`owner-controls-buffer-after-override-${wsId}`}
              style={{ width: 16, height: 16 }} />
            <span className="m-muted small">Override network default</span>
          </div>
          <input type="number" min="0" max="240"
            value={form.buffer_after_override ? form.buffer_after_minutes : nd.buffer_after_minutes}
            disabled={!form.buffer_after_override}
            onChange={e => set("buffer_after_minutes", e.target.value)}
            data-testid={`owner-controls-buffer-after-${wsId}`} />
        </label>
      </div>

      <h4 className="rec-h3" style={{ marginTop: 18, marginBottom: 4 }}>Booking window</h4>
      <div className="m-form-grid-2">
        <label className="m-field"><span>Minimum notice (hours)</span>
          <input type="number" min="0" max="720" value={form.min_notice_hours}
            onChange={e => set("min_notice_hours", e.target.value)}
            data-testid={`owner-controls-min-notice-${wsId}`} />
        </label>
        <label className="m-field"><span>Max advance (days)</span>
          <input type="number" min="1" max="365" value={form.max_advance_days}
            onChange={e => set("max_advance_days", e.target.value)}
            data-testid={`owner-controls-max-advance-${wsId}`} />
        </label>
      </div>

      <h4 className="rec-h3" style={{ marginTop: 18, marginBottom: 4 }}>Cancellation policy</h4>
      <div className="m-amenities" data-testid={`owner-controls-policy-${wsId}`}>
        {["flexible", "moderate", "strict", "custom"].map(p => (
          <button key={p} type="button"
            className={"m-pill light" + (form.cancellation_policy === p ? " active" : "")}
            onClick={() => set("cancellation_policy", p)}
            data-testid={`owner-controls-policy-${p}-${wsId}`}>{p}</button>
        ))}
      </div>
      {form.cancellation_policy !== "custom" && (
        <p className="m-muted small" style={{ marginTop: 6 }}>
          {nd.cancellation_policies[form.cancellation_policy]}
        </p>
      )}
      {form.cancellation_policy === "custom" && (
        <label className="m-field" style={{ marginTop: 10 }}><span>Custom policy text</span>
          <textarea rows={3} value={form.cancellation_policy_text}
            onChange={e => set("cancellation_policy_text", e.target.value)}
            style={{ padding: "12px 14px", border: "1px solid var(--m-border)", borderRadius: 9, background: "var(--cream)", fontSize: 14, resize: "vertical", color: "var(--m-text)" }}
            data-testid={`owner-controls-policy-text-${wsId}`} />
        </label>
      )}

      <div className="m-form-actions" style={{ marginTop: 20 }}>
        <button type="submit" className="m-btn-gold" disabled={saving}
          data-testid={`owner-controls-save-${wsId}`}>
          {saving ? "Saving…" : "Save booking controls"}
        </button>
      </div>
    </form>
  );
}


// Milestone D — Payment schedule editor (Deposit + Balance workflow)
function PaymentScheduleEditor({ wsId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [venueTz, setVenueTz] = useState("America/New_York");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/owner/workspaces/${wsId}/payment-schedule`);
      const s = r.data.schedule || {};
      setForm({
        payment_schedule_type: s.payment_schedule_type || "full_payment_at_booking",
        deposit_type: s.deposit_type || "percentage",
        deposit_percentage: s.deposit_percentage ?? 25,
        deposit_amount_dollars: s.deposit_amount ? (s.deposit_amount / 100) : 0,
        balance_due_timing: s.balance_due_timing || "event_day",
        balance_due_days_before: s.balance_due_days_before ?? 1,
        balance_due_time_local: s.balance_due_time_local || "00:01",
        auto_charge_balance: s.auto_charge_balance !== false,
        deposit_refundable_within_hours: s.deposit_refundable_within_hours ?? 168,
      });
      setVenueTz(r.data.venue_timezone || "America/New_York");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payment schedule");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  if (loading || !form) return <div className="m-loading-inline">Loading payment schedule…</div>;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        payment_schedule_type: form.payment_schedule_type,
        auto_charge_balance: !!form.auto_charge_balance,
        deposit_refundable_within_hours: Number(form.deposit_refundable_within_hours),
        venue_timezone: venueTz,
      };
      if (form.payment_schedule_type === "deposit_then_balance") {
        payload.deposit_type = form.deposit_type;
        if (form.deposit_type === "percentage") {
          payload.deposit_percentage = Number(form.deposit_percentage);
        } else {
          payload.deposit_amount = Math.round(Number(form.deposit_amount_dollars) * 100);
        }
        payload.balance_due_timing = form.balance_due_timing;
        if (form.balance_due_timing === "days_before_event") {
          payload.balance_due_days_before = Number(form.balance_due_days_before);
        }
        payload.balance_due_time_local = form.balance_due_time_local;
      }
      await api.put(`/owner/workspaces/${wsId}/payment-schedule`, payload);
      toast.success("Payment schedule saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isDeposit = form.payment_schedule_type === "deposit_then_balance";

  return (
    <form onSubmit={onSave} className="m-form" data-testid={`owner-payment-schedule-${wsId}`}>
      <h4 className="rec-h3" style={{ marginTop: 4, marginBottom: 4 }}>Payment schedule</h4>
      <p className="m-muted small" style={{ marginTop: -2 }}>
        Choose how customers pay. With deposit + balance, we save the customer's card at checkout
        and auto-charge the remaining amount on the balance due date.
      </p>
      <div className="m-amenities" data-testid={`owner-ps-type-${wsId}`}>
        {[
          {k: "full_payment_at_booking", label: "Full payment at booking"},
          {k: "deposit_then_balance", label: "Deposit + balance"},
        ].map(o => (
          <button key={o.k} type="button"
            className={"m-pill light" + (form.payment_schedule_type === o.k ? " active" : "")}
            onClick={() => set("payment_schedule_type", o.k)}
            data-testid={`owner-ps-type-${o.k}-${wsId}`}>{o.label}</button>
        ))}
      </div>

      {isDeposit && (
        <>
          <h5 style={{ marginTop: 18, marginBottom: 6, fontSize: 13.5, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(27,35,64,0.62)" }}>Deposit amount</h5>
          <div className="m-amenities" data-testid={`owner-ps-deposit-type-${wsId}`}>
            {[{k:"percentage", label:"Percentage"}, {k:"fixed", label:"Fixed amount"}].map(o => (
              <button key={o.k} type="button"
                className={"m-pill light" + (form.deposit_type === o.k ? " active" : "")}
                onClick={() => set("deposit_type", o.k)}
                data-testid={`owner-ps-deposit-type-${o.k}-${wsId}`}>{o.label}</button>
            ))}
          </div>
          {form.deposit_type === "percentage" ? (
            <label className="m-field" style={{ marginTop: 10 }}><span>Deposit % of total</span>
              <input type="number" min="1" max="99" value={form.deposit_percentage}
                onChange={e => set("deposit_percentage", e.target.value)}
                data-testid={`owner-ps-deposit-pct-${wsId}`} />
              <span className="m-muted small">Customer pays this percent up-front; the balance is auto-charged later.</span>
            </label>
          ) : (
            <label className="m-field" style={{ marginTop: 10 }}><span>Deposit amount ($)</span>
              <input type="number" min="1" step="0.01" value={form.deposit_amount_dollars}
                onChange={e => set("deposit_amount_dollars", e.target.value)}
                data-testid={`owner-ps-deposit-amount-${wsId}`} />
              <span className="m-muted small">Customer pays this flat amount up-front. Must be less than the reservation total.</span>
            </label>
          )}

          <h5 style={{ marginTop: 18, marginBottom: 6, fontSize: 13.5, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(27,35,64,0.62)" }}>Balance due</h5>
          <div className="m-amenities" data-testid={`owner-ps-balance-timing-${wsId}`}>
            {[{k:"event_day", label:"On event day"}, {k:"days_before_event", label:"N days before event"}].map(o => (
              <button key={o.k} type="button"
                className={"m-pill light" + (form.balance_due_timing === o.k ? " active" : "")}
                onClick={() => set("balance_due_timing", o.k)}
                data-testid={`owner-ps-timing-${o.k}-${wsId}`}>{o.label}</button>
            ))}
          </div>
          <div className="m-form-grid-2" style={{ marginTop: 10 }}>
            {form.balance_due_timing === "days_before_event" && (
              <label className="m-field"><span>Days before event</span>
                <input type="number" min="0" max="90" value={form.balance_due_days_before}
                  onChange={e => set("balance_due_days_before", e.target.value)}
                  data-testid={`owner-ps-days-before-${wsId}`} />
              </label>
            )}
            <label className="m-field"><span>Time (venue local)</span>
              <input type="time" value={form.balance_due_time_local}
                onChange={e => set("balance_due_time_local", e.target.value)}
                data-testid={`owner-ps-time-${wsId}`} />
              <span className="m-muted small">Venue timezone: {venueTz}</span>
            </label>
          </div>

          <label className="m-field" style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={form.auto_charge_balance}
                onChange={e => set("auto_charge_balance", e.target.checked)}
                data-testid={`owner-ps-auto-charge-${wsId}`}
                style={{ width: 16, height: 16 }} />
              <span>Auto-charge saved payment method on balance due date</span>
            </div>
            <span className="m-muted small">
              Recommended. When enabled, we save the customer's card at deposit checkout and charge
              the balance automatically. If disabled, you'll invoice manually.
            </span>
          </label>

          <label className="m-field" style={{ marginTop: 12 }}><span>Deposit refundable within (hours)</span>
            <input type="number" min="0" max="8760" value={form.deposit_refundable_within_hours}
              onChange={e => set("deposit_refundable_within_hours", e.target.value)}
              data-testid={`owner-ps-refund-window-${wsId}`} />
            <span className="m-muted small">
              If a balance payment ultimately fails and the reservation auto-cancels, the deposit
              is refunded when it was paid within this many hours. 168 = 7 days.
            </span>
          </label>
        </>
      )}

      <div className="m-form-actions" style={{ marginTop: 20 }}>
        <button type="submit" className="m-btn-gold" disabled={saving}
          data-testid={`owner-ps-save-${wsId}`}>
          {saving ? "Saving…" : "Save payment schedule"}
        </button>
      </div>
    </form>
  );
}


function BookingRequestsQueue({ wsId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [filter, setFilter] = useState("pending_request");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ workspace_id: wsId });
      if (filter && filter !== "all") params.set("state", filter);
      const r = await api.get(`/owner/booking-requests?${params.toString()}`);
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, filter]);

  const approve = async (rid) => {
    setBusyId(rid);
    try {
      await api.post(`/owner/booking-requests/${rid}/approve`);
      toast.success("Approved. Member has been emailed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusyId("");
    }
  };

  const decline = async (rid) => {
    const reason = window.prompt("Optional note to the member (leave blank to skip):", "");
    if (reason === null) return; // cancelled
    setBusyId(rid);
    try {
      await api.post(`/owner/booking-requests/${rid}/decline`, { reason: reason || null });
      toast.success("Declined. Member will be refunded if a payment was collected.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decline failed");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div data-testid={`owner-requests-${wsId}`}>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          { k: "pending_request", label: "Pending" },
          { k: "confirmed", label: "Confirmed" },
          { k: "declined", label: "Declined" },
          { k: "expired", label: "Expired" },
          { k: "cancelled", label: "Cancelled" },
          { k: "all", label: "All" },
        ].map(f => (
          <button key={f.k} type="button"
            className={"m-pill light" + (filter === f.k ? " active" : "")}
            onClick={() => setFilter(f.k)}
            data-testid={`owner-requests-filter-${f.k}-${wsId}`}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="m-loading-inline">Loading requests…</div>
      ) : items.length === 0 ? (
        <div className="m-empty small" data-testid={`owner-requests-empty-${wsId}`}>
          {filter === "pending_request"
            ? "No pending requests. When members request to book, they'll appear here for your approval."
            : `No requests in state "${filter}".`}
        </div>
      ) : (
        <div className="m-bookings-list">
          {items.map((r, idx) => {
            const isPending = r.state === "pending_request";
            const expiresLabel = r.request_expires_at
              ? new Date(r.request_expires_at).toLocaleString()
              : null;
            return (
              <div key={r.request_id} className="m-booking-row"
                data-testid={`owner-request-row-${wsId}-${idx}`}>
                <div>
                  <div className="m-booking-ws">{r.member_name || r.member_email}</div>
                  <div className="m-booking-meta">
                    {r.member_email} · <span style={{ textTransform: "uppercase", letterSpacing: 0.12, fontSize: 10.5 }}>
                      {r.state.replace(/_/g, " ")}
                    </span>
                    {expiresLabel && isPending && <> · auto-decline at {expiresLabel}</>}
                    {r.decline_reason && <> · &ldquo;{r.decline_reason}&rdquo;</>}
                  </div>
                </div>
                <div className="m-booking-when">
                  <div className="m-booking-date">{r.date} · {r.start_time} · {r.hours}h</div>
                  <div className="m-booking-slot">
                    {r.payment ? fmtMoney((r.payment.gross_cents || 0) / 100) : ""}
                    {r.payment ? ` · ${fmtMoney((r.payment.venue_allocation_cents || 0) / 100)} payout` : ""}
                  </div>
                  {isPending && (
                    <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                      <button className="m-btn-gold" style={{ padding: "6px 12px", fontSize: 12 }}
                        disabled={busyId === r.request_id}
                        onClick={() => approve(r.request_id)}
                        data-testid={`owner-request-approve-${wsId}-${idx}`}>
                        {busyId === r.request_id ? "…" : "Approve"}
                      </button>
                      <button className="m-btn-ghost" style={{ padding: "6px 12px", fontSize: 12 }}
                        disabled={busyId === r.request_id}
                        onClick={() => decline(r.request_id)}
                        data-testid={`owner-request-decline-${wsId}-${idx}`}>
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ReportsExportBar({ wsId }) {
  // Session 47 · Month picker for CSV export.
  // Defaults to "current" (server-side MTD). Selecting a specific month
  // appends ?month=YYYY-MM to the download URL. We build a rolling list
  // of the last 13 months so hosts can pull any recent period.
  const [choice, setChoice] = useState("current");

  const options = useMemo(() => {
    const opts = [{ value: "current", label: "Month to date (default)" }];
    const now = new Date();
    for (let i = 0; i < 13; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
      opts.push({ value, label: i === 0 ? `${label} (this month)` : label });
    }
    return opts;
  }, []);

  const base = `${process.env.REACT_APP_BACKEND_URL || ""}/api/owner/workspaces/${wsId}/reports/csv`;
  const href = choice === "current" ? base : `${base}?month=${choice}`;
  const label = choice === "current"
    ? "Download MTD CSV"
    : `Download ${options.find(o => o.value === choice)?.label || choice} CSV`;

  return (
    <div style={{ display: "flex", justifyContent: "flex-end",
                  alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
      <label style={{ fontSize: 11, letterSpacing: "0.14em",
                        textTransform: "uppercase", color: "rgba(27,35,64,0.55)",
                        fontWeight: 600, display: "inline-flex",
                        alignItems: "center", gap: 8 }}>
        Report period
        <select
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          className="m-tabs-select"
          style={{ padding: "6px 10px", fontSize: 12, minWidth: 210 }}
          data-testid={`owner-reports-month-${wsId}`}
        >
          {options.map(o => (
            <option key={o.value} value={o.value} data-testid={`owner-reports-month-option-${o.value}`}>{o.label}</option>
          ))}
        </select>
      </label>
      <a
        href={href}
        className="m-btn-ghost"
        style={{ padding: "6px 14px", fontSize: 12, textDecoration: "none",
                  display: "inline-flex", alignItems: "center", gap: 6,
                  borderColor: "var(--gold)", color: "var(--gold)" }}
        data-testid={`owner-reports-csv-${wsId}`}
        download
      >
        <span aria-hidden="true">↓</span> {label}
      </a>
    </div>
  );
}


function ReservationsTable({ bookings, wsId, onCancelled }) {
  const [filter, setFilter] = useState("upcoming"); // upcoming | past | all
  const [busyId, setBusyId] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);

  // Session 47 · Preset cancellation reasons. Selecting a preset fills the
  // free-text field but the host can always edit before confirming.
  // The final `reason` string flows into the audit log + email breakdown.
  const CANCEL_PRESETS = [
    "Double-booked — this slot is no longer available",
    "Host emergency — the workspace is unavailable",
    "Member no-show",
    "Requested by member (off-platform)",
    "Facility maintenance / repair",
  ];

  // Session 44 · derive upcoming vs past from booking `date` (YYYY-MM-DD).
  // Fallback to created_at if date missing. Keep it simple: today's calendar
  // day counts as upcoming.
  const today = new Date().toISOString().slice(0, 10);
  const list = (bookings || []).filter(b => {
    if (filter === "all") return true;
    const d = b.date || (b.created_at || "").slice(0, 10) || "";
    if (filter === "upcoming") return d >= today;
    return d < today;
  });

  const cancel = async (b, reason) => {
    const id = b.request_id || b.booking_id || b.session_id;
    if (!id) {
      toast.error("Missing booking id");
      return;
    }
    const finalReason = (reason || "").trim() || "Cancelled by host from Owner Dashboard";
    setBusyId(id);
    try {
      const endpoint = b.request_id
        ? `/bookings/${b.request_id}/cancel`
        : `/owner/bookings/${b.booking_id || b.session_id}/cancel`;
      await api.post(endpoint, { reason: finalReason });
      toast.success("Reservation cancelled. Refund policy applied · member and host emailed.");
      setCancelTarget(null);
      onCancelled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusyId("");
    }
  };

  const emptyLabel =
    filter === "upcoming"
      ? "No upcoming reservations. Share your space link to fill your calendar."
      : filter === "past"
        ? "No past reservations yet."
        : "No reservations yet. Share your space page link to start receiving bookings.";

  return (
    <div data-testid={`owner-reservations-${wsId}`}>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          { k: "upcoming", label: "Upcoming" },
          { k: "past", label: "Past" },
          { k: "all", label: "All" },
        ].map(f => (
          <button
            key={f.k}
            type="button"
            className={"m-pill light" + (filter === f.k ? " active" : "")}
            onClick={() => setFilter(f.k)}
            data-testid={`owner-reservations-filter-${f.k}-${wsId}`}
          >
            {f.label}
            <em style={{ marginLeft: 6, opacity: 0.6, fontStyle: "normal" }}>
              {f.k === "all" ? (bookings || []).length : (bookings || []).filter(b => {
                const d = b.date || (b.created_at || "").slice(0, 10) || "";
                return f.k === "upcoming" ? d >= today : d < today;
              }).length}
            </em>
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="m-empty small" data-testid={`owner-reservations-empty-${wsId}`}>
          {emptyLabel}
        </div>
      ) : (
        <div className="m-bookings-list">
          {list.map((b, idx) => {
            const id = b.request_id || b.booking_id || b.session_id || idx;
            const d = b.date || (b.created_at || "").slice(0, 10) || "";
            const isPast = d && d < today;
            const isCancellable = !isPast && (b.request_id || b.booking_id || b.session_id);
            return (
              <div key={id} className="m-booking-row"
                   data-testid={`owner-booking-${wsId}-${idx}`}>
                <div>
                  <div className="m-booking-ws">{b.name || b.email}</div>
                  <div className="m-booking-meta">
                    {b.email}
                    {b.confirmation_number ? ` · #${b.confirmation_number}` : ""}
                    {isPast && <span style={{ marginLeft: 8, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(27,35,64,0.55)" }}>Past</span>}
                  </div>
                </div>
                <div className="m-booking-when">
                  <div className="m-booking-date">{b.date} {b.start_time ? `· ${b.start_time}` : b.slot ? `· ${b.slot}` : ""}</div>
                  <div className="m-booking-slot">
                    {b.hours ? `${b.hours}h` : b.duration ? `${b.duration}h` : ""}
                    {b.amount ? ` · ${fmtMoney(b.amount)} → ${fmtMoney(b.host_payout)} payout` : ""}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                    {b.email && (
                      <a
                        href={`mailto:${b.email}?subject=${encodeURIComponent("Your reservation at " + (b.workspace_name || "our space"))}`}
                        className="m-btn-ghost"
                        style={{ padding: "6px 10px", fontSize: 12, textDecoration: "none" }}
                        data-testid={`owner-booking-message-${wsId}-${idx}`}
                      >
                        Message
                      </a>
                    )}
                    {isCancellable && (
                      <button
                        type="button"
                        className="m-btn-ghost"
                        style={{ padding: "6px 10px", fontSize: 12, color: "rgb(180,60,60)", borderColor: "rgba(180,60,60,0.35)" }}
                        disabled={busyId === id}
                        onClick={() => setCancelTarget(b)}
                        data-testid={`owner-booking-cancel-${wsId}-${idx}`}
                      >
                        {busyId === id ? "…" : "Cancel"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {cancelTarget && (
        <CancelReasonModal
          booking={cancelTarget}
          presets={CANCEL_PRESETS}
          busy={busyId === (cancelTarget.request_id || cancelTarget.booking_id || cancelTarget.session_id)}
          onClose={() => setCancelTarget(null)}
          onConfirm={(reason) => cancel(cancelTarget, reason)}
        />
      )}
    </div>
  );
}

// Session 47 · Cancel modal — preset reasons + free-text override.
// Emits a single `reason` string via onConfirm. The server-side email
// pipeline includes this verbatim in both the member and host emails.
function CancelReasonModal({ booking, presets, busy, onClose, onConfirm }) {
  const [selected, setSelected] = useState("");
  const [custom, setCustom] = useState("");
  const finalReason = selected === "other" ? custom : (selected || "");

  const disabled = busy || !finalReason.trim();

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(27,35,64,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 2000, padding: 20,
      }}
      onClick={onClose}
      data-testid="cancel-reason-modal-backdrop"
    >
      <div
        style={{
          background: "#faf8f3", borderRadius: 12,
          maxWidth: 520, width: "100%",
          padding: "22px 26px 20px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="cancel-reason-modal"
      >
        <div className="m-eyebrow" style={{ color: "var(--gold)" }}>Cancel reservation</div>
        <h3 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 22, margin: "6px 0 6px",
        }}>Cancel {booking.name || booking.email}'s booking?</h3>
        <p className="m-muted small" style={{ margin: "0 0 16px", lineHeight: 1.5 }}>
          The member will be refunded per the cancellation policy. Both the
          member and you will receive an email with a refund breakdown and
          the reason below.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
          {presets.map((r) => (
            <label
              key={r}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "10px 12px",
                border: "1px solid " + (selected === r ? "var(--gold)" : "rgba(27,35,64,0.14)"),
                background: selected === r ? "rgba(184,145,58,0.06)" : "#fff",
                borderRadius: 8, cursor: "pointer",
                transition: "background 100ms, border 100ms",
              }}
              data-testid={`cancel-reason-preset-${presets.indexOf(r)}`}
            >
              <input
                type="radio"
                name="cancel-reason"
                checked={selected === r}
                onChange={() => { setSelected(r); setCustom(""); }}
                style={{ marginTop: 3 }}
              />
              <span style={{ fontSize: 13, color: "var(--m-navy)" }}>{r}</span>
            </label>
          ))}
          <label
            style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "10px 12px",
              border: "1px solid " + (selected === "other" ? "var(--gold)" : "rgba(27,35,64,0.14)"),
              background: selected === "other" ? "rgba(184,145,58,0.06)" : "#fff",
              borderRadius: 8, cursor: "pointer",
            }}
            data-testid="cancel-reason-preset-other"
          >
            <input
              type="radio"
              name="cancel-reason"
              checked={selected === "other"}
              onChange={() => setSelected("other")}
              style={{ marginTop: 3 }}
            />
            <span style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "var(--m-navy)", marginBottom: 6 }}>Other — write your own</div>
              <textarea
                rows={2}
                value={custom}
                onChange={(e) => { setCustom(e.target.value); if (selected !== "other") setSelected("other"); }}
                placeholder="Explain the situation so the member has clear context…"
                style={{
                  width: "100%", padding: "8px 10px", fontSize: 13,
                  border: "1px solid rgba(27,35,64,0.20)", borderRadius: 6,
                  background: "#fff", fontFamily: "inherit",
                }}
                data-testid="cancel-reason-custom-input"
              />
            </span>
          </label>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button
            type="button"
            className="m-btn-ghost"
            onClick={onClose}
            disabled={busy}
            style={{ padding: "8px 14px", fontSize: 13 }}
            data-testid="cancel-reason-close"
          >
            Keep booking
          </button>
          <button
            type="button"
            className="m-btn-gold"
            onClick={() => onConfirm(finalReason)}
            disabled={disabled}
            style={{
              padding: "8px 16px", fontSize: 13,
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            data-testid="cancel-reason-confirm"
          >
            {busy ? "Cancelling…" : "Cancel reservation"}
          </button>
        </div>
      </div>
    </div>
  );
}


function WorkspacePanel({ ws, onChange }) {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("customize");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [s, b] = await Promise.all([
          api.get(`/owner/workspaces/${ws.workspace_id}/stats`),
          api.get(`/owner/workspaces/${ws.workspace_id}/bookings`),
        ]);
        if (cancelled) return;
        setStats(s.data); setBookings(b.data);
      } catch {/* ignore */}
    };
    load();
    return () => { cancelled = true; };
  }, [ws.workspace_id]);

  return (
    <section className="m-card owner-panel" data-testid={`owner-panel-${ws.workspace_id}`}>
      <div className="owner-panel-head">
        <div>
          <div className="owner-panel-title">{ws.name}</div>
          <div className="owner-panel-meta">{ws.neighborhood} · {ws.city}</div>
        </div>
        <div className="owner-panel-tabs">
          <button className={"owner-tab" + (tab === "customize" ? " active" : "")} onClick={() => setTab("customize")} data-testid={`owner-tab-customize-${ws.workspace_id}`}>Customize</button>
          <button className={"owner-tab" + (tab === "controls" ? " active" : "")} onClick={() => setTab("controls")} data-testid={`owner-tab-controls-${ws.workspace_id}`}>Booking controls</button>
          <button className={"owner-tab" + (tab === "payments" ? " active" : "")} onClick={() => setTab("payments")} data-testid={`owner-tab-payments-${ws.workspace_id}`}>Payments</button>
          <button className={"owner-tab" + (tab === "requests" ? " active" : "")} onClick={() => setTab("requests")} data-testid={`owner-tab-requests-${ws.workspace_id}`}>Requests</button>
          <button className={"owner-tab" + (tab === "reservations" ? " active" : "")} onClick={() => setTab("reservations")} data-testid={`owner-tab-reservations-${ws.workspace_id}`}>Reservations</button>
          <button className={"owner-tab" + (tab === "reports" ? " active" : "")} onClick={() => setTab("reports")} data-testid={`owner-tab-reports-${ws.workspace_id}`}>Reports</button>
        </div>
      </div>

      {tab === "customize" && (
        <WorkspaceEditor ws={ws} onSaved={onChange} />
      )}

      {tab === "controls" && (
        <BookingControlsEditor wsId={ws.workspace_id} />
      )}

      {tab === "payments" && (
        <PaymentScheduleEditor wsId={ws.workspace_id} />
      )}

      {tab === "requests" && (
        <BookingRequestsQueue wsId={ws.workspace_id} />
      )}

      {tab === "reservations" && (
        <ReservationsTable
          bookings={bookings}
          wsId={ws.workspace_id}
          onCancelled={async () => {
            try {
              const b = await api.get(`/owner/workspaces/${ws.workspace_id}/bookings`);
              setBookings(b.data);
            } catch { /* ignore */ }
          }}
        />
      )}

      {tab === "reports" && stats && (
        <div data-testid={`owner-reports-${ws.workspace_id}`}>
          <ReportsExportBar wsId={ws.workspace_id} />
          <div className="m-stats-grid">
            <div className="m-stat"><span className="m-stat-num">{stats.member_bookings_count}</span><span className="m-stat-label">Member bookings</span></div>
            <div className="m-stat"><span className="m-stat-num">{stats.paid_bookings_count}</span><span className="m-stat-label">Paid bookings</span></div>
            <div className="m-stat"><span className="m-stat-num">{fmtMoney(stats.gross_booking_value ?? stats.total_gross)}</span><span className="m-stat-label">Gross booking value</span></div>
            <div className="m-stat"><span className="m-stat-num">{fmtMoney(stats.platform_fee_total ?? stats.total_fees)}</span><span className="m-stat-label">Métier fee</span></div>
            <div className="m-stat" data-testid={`owner-net-payout-${ws.workspace_id}`}>
              <span className="m-stat-num">{fmtMoney(stats.net_owner_payout ?? stats.total_payout)}</span>
              <span className="m-stat-label">Net payout</span>
            </div>
          </div>
          {(stats.refund_total > 0 || stats.processing_fee_total > 0) && (
            <div className="m-stats-grid" style={{ marginTop: 8 }}>
              {stats.processing_fee_total > 0 && (
                <div className="m-stat"><span className="m-stat-num">{fmtMoney(stats.processing_fee_total)}</span><span className="m-stat-label">Processing fees</span></div>
              )}
              {stats.refund_total > 0 && (
                <div className="m-stat"><span className="m-stat-num">{fmtMoney(stats.refund_total)}</span><span className="m-stat-label">Refunds issued</span></div>
              )}
            </div>
          )}
          <p className="m-muted small" style={{ marginTop: 12 }}>
            Your payout reflects the booking total minus applicable Métier fees, payment processing fees, refunds, and adjustments.
          </p>
        </div>
      )}
    </section>
  );
}

function AddWorkspaceModal({ venueId, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", workspace_type: "Coworking Space", description: "", capacity: 1,
    hourly: 0, daily: 0, status: "draft",
    booking_mode: "instant", buffer_before_minutes: 15, buffer_after_minutes: 15,
    min_notice_hours: 2, max_advance_days: 90,
    cancellation_policy: "moderate",
    // Milestone A · Pricing V2
    hourly_enabled: true, hourly_rate: 15,
    half_day_enabled: false, half_day_rate: 50, half_day_duration_hours: 4,
    full_day_enabled: false, full_day_rate: 90, full_day_duration_hours: 8,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const pricingBlock = {
        currency: "USD",
        hourly_enabled: !!f.hourly_enabled,
        hourly_rate: f.hourly_enabled ? Number(f.hourly_rate) : null,
        half_day_enabled: !!f.half_day_enabled,
        half_day_rate: f.half_day_enabled ? Number(f.half_day_rate) : null,
        half_day_duration_hours: f.half_day_enabled ? Number(f.half_day_duration_hours) : null,
        full_day_enabled: !!f.full_day_enabled,
        full_day_rate: f.full_day_enabled ? Number(f.full_day_rate) : null,
        full_day_duration_hours: f.full_day_enabled ? Number(f.full_day_duration_hours) : null,
      };
      // Client-side validation matching backend rules
      if (pricingBlock.half_day_enabled && pricingBlock.full_day_enabled &&
          pricingBlock.half_day_duration_hours >= pricingBlock.full_day_duration_hours) {
        toast.error("Half-day duration must be shorter than full-day duration.");
        setSaving(false); return;
      }
      const payload = { ...f,
        capacity: Number(f.capacity),
        hourly: f.hourly_enabled ? Number(f.hourly_rate) : (Number(f.hourly) || null),
        daily: f.full_day_enabled ? Number(f.full_day_rate) : (Number(f.daily) || null),
        pricing: pricingBlock,
        buffer_before_minutes: Number(f.buffer_before_minutes),
        buffer_after_minutes: Number(f.buffer_after_minutes),
        min_notice_hours: Number(f.min_notice_hours),
        max_advance_days: Number(f.max_advance_days),
      };
      const r = await api.post(`/owner/venues/${venueId}/workspaces`, payload);
      toast.success(`Workspace "${r.data.name}" saved as ${r.data.status || "draft"}.`);
      onCreated(r.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="m-welcome-backdrop" role="dialog" aria-modal="true"
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="add-workspace-modal">
      <div className="m-welcome-card" style={{ maxWidth: 640, maxHeight: "90vh", overflowY: "auto" }}>
        <button type="button" className="m-welcome-close" aria-label="Close"
          onClick={onClose} data-testid="add-workspace-close">×</button>
        <div style={{ padding: "8px 32px 24px" }}>
          <span className="m-eyebrow">New workspace</span>
          <h2 className="m-h2" style={{ marginTop: 4 }}>Add a bookable workspace.</h2>
          <p className="m-muted small">Each workspace has its own rate, availability, and rules. You can save as a draft and publish later.</p>
          <form className="m-form" onSubmit={submit} style={{ marginTop: 12 }}>
            <div className="m-form-grid-2">
              <label className="m-field"><span>Name</span>
                <input required maxLength={120} value={f.name} onChange={e => set("name", e.target.value)}
                  data-testid="add-ws-name" placeholder="Conference Room A" /></label>
              <label className="m-field"><span>Type</span>
                <select value={f.workspace_type} onChange={e => set("workspace_type", e.target.value)}
                  data-testid="add-ws-type"
                  style={{ padding: "10px 14px", border: "1px solid var(--m-border)", borderRadius: 9, background: "var(--cream)", color: "var(--m-text)" }}>
                  {["Coworking Space","Event Venue","Meeting Room","Private Office"].map(t =>
                    <option key={t} value={t}>{t}</option>)}
                </select></label>
            </div>
            <label className="m-field" style={{ marginTop: 10 }}><span>Description</span>
              <textarea rows={2} value={f.description} onChange={e => set("description", e.target.value)}
                data-testid="add-ws-desc"
                style={{ padding: "10px 14px", border: "1px solid var(--m-border)", borderRadius: 9, background: "var(--cream)", color: "var(--m-text)", resize: "vertical" }} />
            </label>
            <div className="m-form-grid-2" style={{ marginTop: 10 }}>
              <label className="m-field"><span>Capacity</span>
                <input type="number" min={0} max={5000} value={f.capacity}
                  onChange={e => set("capacity", e.target.value)} data-testid="add-ws-capacity" /></label>
              <label className="m-field"><span>Status</span>
                <select value={f.status} onChange={e => set("status", e.target.value)}
                  data-testid="add-ws-status"
                  style={{ padding: "10px 14px", border: "1px solid var(--m-border)", borderRadius: 9, background: "var(--cream)", color: "var(--m-text)" }}>
                  <option value="draft">Draft (hidden from marketplace)</option>
                  <option value="active">Active (published)</option>
                </select></label>
            </div>

            <h4 className="rec-h3" style={{ marginTop: 18, marginBottom: 4 }}>Pricing</h4>
            <p className="m-muted small" style={{ marginTop: -2 }}>
              Enable any combination. Half-day and full-day are flat rates for a fixed length.
              Anything left off inherits from your venue defaults.
            </p>

            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
              <input type="checkbox" checked={!!f.hourly_enabled}
                onChange={e => set("hourly_enabled", e.target.checked)}
                data-testid="add-ws-hourly-enabled" style={{ width: 16, height: 16 }} />
              <span>Enable hourly bookings</span>
            </label>
            {f.hourly_enabled && (
              <label className="m-field" style={{ marginTop: 4 }}><span>Hourly rate ($)</span>
                <input type="number" min={0} step="0.01" value={f.hourly_rate}
                  onChange={e => set("hourly_rate", e.target.value)}
                  data-testid="add-ws-hourly-rate" /></label>
            )}

            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <input type="checkbox" checked={!!f.half_day_enabled}
                onChange={e => set("half_day_enabled", e.target.checked)}
                data-testid="add-ws-halfday-enabled" style={{ width: 16, height: 16 }} />
              <span>Enable half-day bookings</span>
            </label>
            {f.half_day_enabled && (
              <div className="m-form-grid-2">
                <label className="m-field"><span>Half-day rate ($)</span>
                  <input type="number" min={0} step="0.01" value={f.half_day_rate}
                    onChange={e => set("half_day_rate", e.target.value)}
                    data-testid="add-ws-halfday-rate" /></label>
                <label className="m-field"><span>Half-day length (hours)</span>
                  <input type="number" min={0.5} max={24} step="0.5" value={f.half_day_duration_hours}
                    onChange={e => set("half_day_duration_hours", e.target.value)}
                    data-testid="add-ws-halfday-dur" /></label>
              </div>
            )}

            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <input type="checkbox" checked={!!f.full_day_enabled}
                onChange={e => set("full_day_enabled", e.target.checked)}
                data-testid="add-ws-fullday-enabled" style={{ width: 16, height: 16 }} />
              <span>Enable full-day bookings</span>
            </label>
            {f.full_day_enabled && (
              <div className="m-form-grid-2">
                <label className="m-field"><span>Full-day rate ($)</span>
                  <input type="number" min={0} step="0.01" value={f.full_day_rate}
                    onChange={e => set("full_day_rate", e.target.value)}
                    data-testid="add-ws-fullday-rate" /></label>
                <label className="m-field"><span>Full-day length (hours)</span>
                  <input type="number" min={0.5} max={24} step="0.5" value={f.full_day_duration_hours}
                    onChange={e => set("full_day_duration_hours", e.target.value)}
                    data-testid="add-ws-fullday-dur" /></label>
              </div>
            )}

            {f.half_day_enabled && f.full_day_enabled &&
              Number(f.half_day_rate) > Number(f.full_day_rate) && (
                <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(184,145,58,0.1)",
                              border: "1px solid rgba(184,145,58,0.35)", borderRadius: 6,
                              fontSize: 13, color: "rgba(27,35,64,0.85)" }}
                     data-testid="add-ws-half-gt-full-warn">
                  Heads up: your half-day rate is higher than your full-day rate. That's allowed
                  (e.g. peak vs off-peak) — just wanted to flag it.
                </div>
              )}
            <h4 className="rec-h3" style={{ marginTop: 16, marginBottom: 4 }}>Booking rules</h4>
            <div className="m-amenities" data-testid="add-ws-mode">
              {["instant", "request"].map(m => (
                <button key={m} type="button"
                  className={"m-pill light" + (f.booking_mode === m ? " active" : "")}
                  onClick={() => set("booking_mode", m)}
                  data-testid={`add-ws-mode-${m}`}>{m === "instant" ? "Instant book" : "Request to book"}</button>
              ))}
            </div>
            <div className="m-form-grid-2" style={{ marginTop: 10 }}>
              <label className="m-field"><span>Buffer before (min)</span>
                <input type="number" min={0} max={240} value={f.buffer_before_minutes}
                  onChange={e => set("buffer_before_minutes", e.target.value)}
                  data-testid="add-ws-buffer-before" /></label>
              <label className="m-field"><span>Buffer after (min)</span>
                <input type="number" min={0} max={240} value={f.buffer_after_minutes}
                  onChange={e => set("buffer_after_minutes", e.target.value)}
                  data-testid="add-ws-buffer-after" /></label>
              <label className="m-field"><span>Min notice (hours)</span>
                <input type="number" min={0} max={720} value={f.min_notice_hours}
                  onChange={e => set("min_notice_hours", e.target.value)}
                  data-testid="add-ws-min-notice" /></label>
              <label className="m-field"><span>Max advance (days)</span>
                <input type="number" min={1} max={365} value={f.max_advance_days}
                  onChange={e => set("max_advance_days", e.target.value)}
                  data-testid="add-ws-max-advance" /></label>
            </div>
            <label className="m-field" style={{ marginTop: 10 }}><span>Cancellation policy</span>
              <div className="m-amenities" data-testid="add-ws-policy">
                {["flexible","moderate","strict"].map(p => (
                  <button key={p} type="button"
                    className={"m-pill light" + (f.cancellation_policy === p ? " active" : "")}
                    onClick={() => set("cancellation_policy", p)}
                    data-testid={`add-ws-policy-${p}`}>{p}</button>
                ))}
              </div>
            </label>
            <div className="m-form-actions" style={{ marginTop: 18 }}>
              <button type="button" className="m-btn-ghost" onClick={onClose}
                disabled={saving} data-testid="add-ws-cancel">Cancel</button>
              <button type="submit" className="m-btn-gold" disabled={saving || !f.name.trim()}
                data-testid="add-ws-submit">
                {saving ? "Saving…" : "Save workspace"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


function WorkspaceRowActions({ venueId, ws, onChanged, onDeleted }) {
  const [busy, setBusy] = useState("");
  const call = async (action, label) => {
    setBusy(action);
    try {
      if (action === "delete") {
        if (!window.confirm(`Delete "${ws.name}" permanently? This cannot be undone.`)) { setBusy(""); return; }
        await api.delete(`/owner/venues/${venueId}/workspaces/${ws.workspace_id}`);
        toast.success(`Deleted "${ws.name}"`);
        onDeleted(ws.workspace_id);
      } else if (action === "duplicate") {
        const r = await api.post(`/owner/venues/${venueId}/workspaces/${ws.workspace_id}/duplicate`);
        toast.success(`Duplicated as draft: "${r.data.name}"`);
        onChanged();
      } else {
        const r = await api.post(`/owner/venues/${venueId}/workspaces/${ws.workspace_id}/${action}`);
        toast.success(label || "Updated");
        onChanged();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (action === "delete" && e?.response?.status === 409) {
        if (window.confirm(detail + "\n\nArchive instead?")) {
          await call("archive", "Archived");
        } else {
          toast.error(detail);
        }
      } else {
        toast.error(detail || `${action} failed`);
      }
    } finally {
      setBusy("");
    }
  };
  const isActive = (ws.status || "active") === "active";
  const isArchived = (ws.status || "") === "archived";
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      <button className="m-btn-ghost" disabled={!!busy}
        onClick={() => call("duplicate", "Duplicated")}
        data-testid={`ws-duplicate-${ws.workspace_id}`}>Duplicate</button>
      {isActive ? (
        <button className="m-btn-ghost" disabled={!!busy}
          onClick={() => call("deactivate", "Paused")}
          data-testid={`ws-deactivate-${ws.workspace_id}`}>Deactivate</button>
      ) : !isArchived ? (
        <button className="m-btn-ghost" disabled={!!busy}
          onClick={() => call("reactivate", "Reactivated")}
          data-testid={`ws-reactivate-${ws.workspace_id}`}>Reactivate</button>
      ) : null}
      {!isArchived && (
        <button className="m-btn-ghost" disabled={!!busy}
          onClick={() => call("archive", "Archived")}
          data-testid={`ws-archive-${ws.workspace_id}`}>Archive</button>
      )}
      <button className="m-btn-ghost" disabled={!!busy}
        onClick={() => call("delete", "Deleted")}
        data-testid={`ws-delete-${ws.workspace_id}`}
        style={{ color: "#b04040" }}>Delete</button>
    </div>
  );
}


function VenueCard({ venue }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/owner/venues/${venue.location_id}/workspaces?include_archived=true`);
      setWorkspaces(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load workspaces");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [venue.location_id]);

  const toggle = (wid) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(wid)) next.delete(wid); else next.add(wid);
      return next;
    });
  };

  return (
    <section className="m-card owner-panel" data-testid={`venue-card-${venue.location_id}`}>
      <div className="owner-panel-head">
        <div>
          <div className="owner-panel-title" data-testid={`venue-name-${venue.location_id}`}>
            {venue.name}
          </div>
          <div className="owner-panel-meta">
            {venue.address || venue.neighborhood}{venue.city ? ` · ${venue.city}` : ""}
            {typeof venue.workspaces_count === "number" && (
              <> · {venue.workspaces_count} workspace{venue.workspaces_count === 1 ? "" : "s"}</>
            )}
          </div>
        </div>
        <button className="m-btn-gold" onClick={() => setShowAdd(true)}
          data-testid={`venue-add-workspace-${venue.location_id}`}>+ Add workspace</button>
      </div>

      <div style={{ marginTop: 14 }}>
        <h4 className="rec-h3" style={{ margin: "8px 0 10px" }}>Workspaces</h4>
        {loading ? (
          <div className="m-loading-inline">Loading workspaces…</div>
        ) : workspaces.length === 0 ? (
          <div className="m-empty small" data-testid={`venue-empty-${venue.location_id}`}>
            No workspaces yet. Click <b>+ Add workspace</b> to create your first bookable unit.
          </div>
        ) : (
          <div className="m-bookings-list">
            {workspaces.map(w => {
              const status = w.status || "active";
              const statusColor = status === "active" ? "#2e7d32"
                : status === "draft" ? "#946400" : status === "paused" ? "#b04040" : "rgba(27,35,64,0.5)";
              const isExpanded = expanded.has(w.workspace_id);
              return (
                <div key={w.workspace_id} className="m-booking-row" style={{ flexDirection: "column", alignItems: "stretch" }} data-testid={`venue-ws-row-${w.workspace_id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, width: "100%" }}>
                    <div>
                      <div className="m-booking-ws" data-testid={`venue-ws-name-${w.workspace_id}`}>{w.name}</div>
                      <div className="m-booking-meta">
                        {w.type} · capacity {w.capacity || "—"} · ${w.hourly || 0}/hr
                        {w.daily > 0 && <> · ${w.daily}/day</>}
                        &nbsp;·&nbsp;<span style={{ color: statusColor, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.14, fontSize: 10.5 }}>
                          {status}
                        </span>
                        {(w.owner_booking_settings?.booking_mode) && <> · {w.owner_booking_settings.booking_mode === "instant" ? "Instant" : "Request"}</>}
                      </div>
                    </div>
                    <button className="m-btn-ghost" onClick={() => toggle(w.workspace_id)}
                      data-testid={`venue-ws-expand-${w.workspace_id}`}>
                      {isExpanded ? "Hide" : "Manage"}
                    </button>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <WorkspaceRowActions venueId={venue.location_id} ws={w}
                      onChanged={load} onDeleted={() => load()} />
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(27,35,64,0.08)" }}>
                      <WorkspacePanel ws={w} onChange={() => load()} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAdd && (
        <AddWorkspaceModal venueId={venue.location_id}
          onClose={() => setShowAdd(false)}
          onCreated={() => load()} />
      )}
    </section>
  );
}


export default function OwnerDashboard() {
  const { user } = useAuth();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  // Session 60.2 (Batch D) · First-login OwnerTour. Auto-opens when a newly
  // assigned owner arrives from the "Manage Your Workspace" email link
  // (`?tour=1`) OR when they simply have not completed the tour yet on any
  // of their venues. Fully dismissible + resumable via localStorage.
  const [tourVenueId, setTourVenueId] = useState(null);
  const [forceTour, setForceTour] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const r = await api.get("/owner/venues");
      setVenues(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load venues");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Pick a venue to attach the tour to (first venue if none picked yet).
  useEffect(() => {
    if (!tourVenueId && venues.length > 0) {
      setTourVenueId(venues[0].location_id);
    }
  }, [venues, tourVenueId]);

  // Query param `?tour=1` forces the tour open even if previously dismissed.
  useEffect(() => {
    if (searchParams.get("tour") === "1") {
      setForceTour(true);
      // Clean the URL so a refresh doesn't force the tour again.
      const clean = new URLSearchParams(searchParams);
      clean.delete("tour");
      setSearchParams(clean, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const isAuthorized = user && (user.role === "admin" || user.is_owner);

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      {isAuthorized && tourVenueId && (
        <OwnerTour venueId={tourVenueId} forceOpen={forceTour} onClose={() => setForceTour(false)} />
      )}
      <div className="m-page-pad">
        <span className="m-eyebrow">Host operations</span>
        <h1 className="m-h1">Your <em>venues</em>.</h1>
        <p className="m-lede">Manage each venue and every workspace within it. Set availability, pricing, and booking rules, all from one place.</p>

        {isAuthorized && venues.length > 0 && <FinancialsCard />}

        {!isAuthorized ? (
          <div className="m-empty" data-testid="owner-empty-unauthorized">
            You don&apos;t have any approved venues yet. Apply at{" "}
            <Link to="/list-your-space" style={{ color: "var(--gold)" }}>list your space</Link> and we&apos;ll grant access here within 48 hours.
          </div>
        ) : loading ? (
          <div className="m-loading-inline">Loading your venues…</div>
        ) : venues.length === 0 ? (
          <div className="m-empty" data-testid="owner-empty">
            No venues are linked to your account yet. If you just applied, give us 48 hours to approve.
          </div>
        ) : (
          <div className="owner-stack" data-testid="owner-stack">
            {venues.map(v => (
              <VenueCard key={v.location_id} venue={v} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}


// ---------------------------------------------------------------------------
// Session 42 · Financial summary card
// ---------------------------------------------------------------------------
// Reads /api/owner/financials (webhook-derived ledger — never calls Stripe
// live) and renders a compact rollup: totals + top 3 venues + top 3 months.

function FinancialsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState("month");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/owner/financials?group_by=${groupBy}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [groupBy]);

  const totals = data?.totals;
  const byVenue = (data?.by_venue || []).slice().sort((a, b) => b.gross - a.gross);
  const byPeriod = (data?.by_period || []).slice(-6);  // last 6 periods

  return (
    <section className="m-card" data-testid="owner-financials-card"
             style={{ padding: "24px 28px", marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
                    gap: 16, marginBottom: 16 }}>
        <div>
          <span className="m-eyebrow" style={{ color: "var(--gold)" }}>Financials</span>
          <h3 style={{ margin: "4px 0 0", fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 24, fontWeight: 500 }}>
            Rollup by <em style={{ color: "var(--gold)" }}>{groupBy === "day" ? "day" : "month"}</em>
          </h3>
          <p className="m-muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
            Source: stored webhook ledger. Never queries Stripe live.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => setGroupBy("month")}
                  data-testid="financials-group-month"
                  className={groupBy === "month" ? "m-btn-gold" : "m-btn-ghost"}
                  style={{ padding: "6px 14px", fontSize: 12 }}>Month</button>
          <button type="button" onClick={() => setGroupBy("day")}
                  data-testid="financials-group-day"
                  className={groupBy === "day" ? "m-btn-gold" : "m-btn-ghost"}
                  style={{ padding: "6px 14px", fontSize: 12 }}>Day</button>
        </div>
      </div>

      {loading ? (
        <div className="m-loading-inline" data-testid="financials-loading">Loading financials…</div>
      ) : !totals ? (
        <div className="m-empty" data-testid="financials-empty">
          No financial data available yet.
        </div>
      ) : (
        <>
          <div className="owner-financials-grid" data-testid="financials-totals"
               style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                         gap: 14, marginBottom: 20 }}>
            <_Stat label="Gross booking value" value={fmtMoney(totals.gross)} />
            <_Stat label="Platform fee" value={fmtMoney(totals.platform_fee)} tone="dim" />
            <_Stat label="Processing fee" value={fmtMoney(totals.processing_fee)} tone="dim" />
            <_Stat label="Refunds" value={fmtMoney(totals.refunds)} tone="warn" />
            <_Stat label="Net owner payout" value={fmtMoney(totals.net_owner_payout)} tone="good" />
            <_Stat label="Bookings" value={String(totals.bookings_total || 0)} />
          </div>

          {byPeriod.length > 0 && (
            <>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em",
                             color: "rgba(27,35,64,0.55)", marginBottom: 8 }}>
                Recent periods
              </div>
              <div style={{ overflow: "auto" }}>
                <table data-testid="financials-by-period"
                       style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "rgba(27,35,64,0.55)" }}>
                      <th style={{ padding: "6px 4px" }}>Period</th>
                      <th style={{ padding: "6px 4px" }}>Gross</th>
                      <th style={{ padding: "6px 4px" }}>Fees</th>
                      <th style={{ padding: "6px 4px" }}>Refunds</th>
                      <th style={{ padding: "6px 4px" }}>Net</th>
                      <th style={{ padding: "6px 4px", textAlign: "right" }}>Bookings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byPeriod.map(row => (
                      <tr key={row.period} style={{ borderTop: "1px solid rgba(27,35,64,0.08)" }}>
                        <td style={{ padding: "8px 4px", fontFamily: "monospace" }}>{row.period}</td>
                        <td style={{ padding: "8px 4px" }}>{fmtMoney(row.gross)}</td>
                        <td style={{ padding: "8px 4px" }}>{fmtMoney(row.platform_fee + row.processing_fee)}</td>
                        <td style={{ padding: "8px 4px" }}>{fmtMoney(row.refunds)}</td>
                        <td style={{ padding: "8px 4px", fontWeight: 600 }}>{fmtMoney(row.net_owner_payout)}</td>
                        <td style={{ padding: "8px 4px", textAlign: "right" }}>{row.bookings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {byVenue.length > 1 && (
            <div style={{ marginTop: 18, fontSize: 12, color: "rgba(27,35,64,0.55)" }}
                 data-testid="financials-by-venue-count">
              Aggregated across <strong>{byVenue.length}</strong> venue{byVenue.length === 1 ? "" : "s"}.
              Top: <strong>{byVenue[0]?.venue_name || "—"}</strong> · {fmtMoney(byVenue[0]?.gross || 0)} gross.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function _Stat({ label, value, tone }) {
  const color = tone === "good" ? "#146F3B"
              : tone === "warn" ? "#B03030"
              : tone === "dim"  ? "rgba(27,35,64,0.55)"
              : "var(--m-navy)";
  return (
    <div style={{ padding: "12px 14px", background: "rgba(27,35,64,0.03)",
                  border: "1px solid rgba(27,35,64,0.08)", borderRadius: 8 }}>
      <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.14em",
                    color: "rgba(27,35,64,0.55)" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}
